import { createAction } from 'redux-actions';
import { MATFILE_ENDLESS_TYPE } from 'pxc-editor-core/constants';
import { getAllLayers, applyPageWidthToPage } from 'pxc-editor-core/helpers/schema';
import {
  SET_PAGE_RESIZE_TYPE,
  SET_PAGE_LENGTH,
  ENDLESS_BASED_ON_CONTENT,
  ENDLESS_FIXED,
  ZOOM_OUT_STATE
} from '@/constants';
import { getShield, recalcZoomBordersScaleCoefs } from '@/helpers';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import {
  createSnapshotThunk,
  handleResizeBasedOnContent,
  setPageChildrenBasedOnContent,
} from '@/actions';
import { centerViewbox, handleZoomClick, handleZoomStateChange } from './rendererView';
import { setWizardData } from './main';
import { setGlobalPageSettingsAction } from '@/store/actions/app-settings';

export const setPageResizeTypeAction = (payload) => createAction(SET_PAGE_RESIZE_TYPE, () => payload)();

export const setPageResizeType = (type, pageId) => (dispatch, getState) => {
  const { core: { pages, pagesSelection } } = getState();
  let selPageId = pageId;

  if (!selPageId) {
    let selPage = pages[pagesSelection[0]];
    if (selPage) {
      selPageId = selPage.uid;
    }
  }
  if (selPageId) {
    dispatch(setPageResizeTypeAction({ type, pageId: selPageId }));
  }
  
};

export const setProjectPrintingAreaSizeThunk = ({ uid, type, length }) => (dispatch, getState) => {
  dispatch(createSnapshotThunk());
  dispatch(setGlobalPageSettingsAction({ resizeType: type, length }))

  switch (type) {
    case ENDLESS_BASED_ON_CONTENT: {
      dispatch(setPageResizeType(type, uid));
      dispatch(setPageChildrenBasedOnContent(uid, true));
      dispatch(checkPageContentLength(uid));
      dispatch(handleResizeBasedOnContent());

      break;
    }
    case ENDLESS_FIXED: {
      dispatch(setPageResizeType(type, uid));
      dispatch(setPageLength(length, uid));

      break;
    }
    default: {
      break;
    }
  }
};

export const setUnsafePageLength = (length, pageId) => (dispatch, getState) => {
  const { core: { pages } } = getState();
  const { endless } = pages[pageId];

  const minlength = endless?.length?.minimum || 1;
  const maxlength = endless?.length?.maximum || 3000;

  let correctLength = length;
  if (correctLength > maxlength) {
    correctLength = maxlength;
  } else if (correctLength < minlength) {
    correctLength = minlength;
  }

  dispatch(setPageLength(correctLength, pageId));
};

export const checkPageContentLength = (pageUid) => (dispatch, getState) => {
  const matfileTemplate = getBuildedMatfileTemplate();
  const { type } = matfileTemplate;
  const { core: { pages, selWizard, sectionsSelection } } = getState();
  const page = pages[pageUid];

  let selectedShield = null;
  if (page?.layouts) {
    for (const secId in sectionsSelection) {
      const { section } = getAllLayers(secId, pages);
      selectedShield = getShield(page.layouts, section.shieldId)
      break;
    }
  }

  if (type === MATFILE_ENDLESS_TYPE) {
    const {
      length: { minimum, maximum },
    } = page?.endless || {length: { minimum: 7, maximum: 2200 }};
    let rightSection;
    let maxX = -1;

    if (page && selWizard?.application_type === 'CABLE_FLAG') {
      let _shields = [];
      for (const layoutId in page.layouts) {
        const layout = page.layouts[layoutId];
        for (const groupId in layout.groups) {
          const group = layout.groups[groupId];
          if (group.shields && Object.keys(group.shields).length === 2) {
            for (const shieldId in group.shields) {
              let maxX = 0;
              let newShieldLength = minimum;
              for (let secId in page.sections) {
                const sec = page.sections[secId];
                if (sec.shieldId === shieldId) {
                  const secMaxX = sec.x + sec.width - group.shields[shieldId].primitiveProps.x;
                  if (secMaxX >= maxX) {
                    maxX = secMaxX;
                    newShieldLength = maxX;
                  }
                  if(selWizard?.data?.isFlagLengthAuto) {
                    group.shields[shieldId].primitiveProps.w = Math.ceil(newShieldLength * 10) / 10;
                  }
                }
              }
              _shields.push(group.shields[shieldId]);
            }
            const flagMidLength = Math.ceil(selWizard.data.flagMidLength);
            _shields = _shields.sort((a, b) => a.primitiveProps.x - b.primitiveProps.x);
            const leftShield = group.shields[_shields[0].id];
            const rightShield = group.shields[_shields[1].id];
            const newShieldLength = selWizard?.data?.isCableFlagSync?
              selectedShield?.id === rightShield.id? rightShield.primitiveProps.w : leftShield.primitiveProps.w
              :
              leftShield.primitiveProps.w >= rightShield.primitiveProps.w? leftShield.primitiveProps.w : rightShield.primitiveProps.w;

            const rightOriginX = rightShield.primitiveProps.x;
            const rightNewX = leftShield.primitiveProps.x + newShieldLength + flagMidLength;
            group.shields[leftShield.id].primitiveProps.w = newShieldLength;
            group.shields[rightShield.id].primitiveProps.x = rightNewX;
            group.shields[rightShield.id].primitiveProps.w = newShieldLength;
            selWizard.data.flagLength = Math.ceil(newShieldLength * 10) / 10;
            selWizard.data.pages = selWizard.data.pages? selWizard.data.pages : {};
            selWizard.data.pages[pageUid] = {
              flagLength: Math.ceil(newShieldLength * 10) / 10
            };
            for (let secId in page.sections) {
              const sec = page.sections[secId];
              if (sec.shieldId === rightShield.id) {
                sec.x = sec.x + (rightNewX - rightOriginX);
              }
            }
            dispatch(setWizardData(selWizard));
            dispatch(setPageLength(newShieldLength * 2 + flagMidLength, page.uid));
            recalcZoomBordersScaleCoefs(newShieldLength * 2 + flagMidLength); 

            dispatch(handleZoomStateChange(ZOOM_OUT_STATE));
            dispatch(handleZoomClick());
            dispatch(centerViewbox());
            break;
          }
          break;
        }
      }
    } else if (page?.resizeType === ENDLESS_BASED_ON_CONTENT) {
      for (let secId in page.sections) {
        const sec = page.sections[secId];
        const secMaxX = sec.x + sec.width;
        if (secMaxX > maxX) {
          rightSection = sec;
          maxX = secMaxX;
        }
      }
      if (rightSection) {
        const newPageLength = Math.min(
          Math.max(
            rightSection.x + rightSection.width,
            minimum,
          ),
          maximum,
        );
        dispatch(setPageLength(newPageLength, page.uid));
      }
    }
  }
};

export const setPageLength = (length, pageId) => (dispatch, getState) => {
  const { core: { pages, selWizard } } = getState();

  if (pageId) {
    const page = pages[pageId];
    const updatedPage = updatePageLength({ length, page, selWizard });

    dispatch(createAction(SET_PAGE_LENGTH, () => ({ page: updatedPage }))());
  }
};

const updatePageLength = ({ length, page, selWizard }) => {
    let shield = null;
    let firstLayout = null;
    for (const layoutId in page.layouts) {
        firstLayout = page.layouts[layoutId];
        break;
    }
    let firstGroup = null;
    for (const groupId in firstLayout.groups) {
        firstGroup = firstLayout.groups[groupId];
        break;
    }
    for (const shieldId in firstGroup.shields) {
        shield = firstGroup.shields[shieldId];
        break;
    }
    if (shield?.isMatrix) {
      const oldLength = shield.primitiveProps.w;
      const shieldX = shield.primitiveProps.x;
      const scaleFactor = length / oldLength;
      const sections = JSON.parse(JSON.stringify(page.sections));
      if (!shield?.isMatrixFixedCellWidth) {
        Object.values(sections).forEach(section => {
          const offsetX = section.x >= shieldX ? section.x - shieldX : 0;
          section.x = Number((shieldX + offsetX * scaleFactor).toFixed(10));
          section.width = section.width > 0? Number((section.width * scaleFactor).toFixed(10)) : 0;
          if (section.children) {
            section.children.forEach(child => {
              const childX = (child.x - shieldX);
              child.x = Number((shieldX + childX * scaleFactor).toFixed(10));
              child.width = Number((child.width * scaleFactor).toFixed(10));
            });
          }
        });
        page.sections = sections;
      }
    }

    return applyPageWidthToPage(length, page, selWizard);
};
